import React, { useState, useCallback, useMemo } from 'react';
import { IUser, UserRole, AuthMethod } from '@skystone/shared';
import { userApi } from '@/services/user-api';
import { formatDateTime } from '@/lib/date-utils';
import { DataTable, ColumnDef } from '@/components/ui/DataTable';
import { Button, Badge, TrashIcon, PageHeader, PageLoading, ErrorBanner, Dialog, DialogFooter } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { getErrorMessage } from '@/lib/utils';
import { inputClass } from '@/lib/form-styles';
import { PageContainer } from '@/components/layout';

// ── Module-level column factory ───────────────────────────────────────────────

function createUserColumns(
  onRoleChange: (user: IUser, newRole: UserRole) => Promise<void>,
  onDelete: (user: IUser) => Promise<void>,
): ColumnDef<IUser>[] {
  return [
    {
      id: 'email',
      header: 'Email',
      accessor: 'email',
      sortable: true,
      filter: { type: 'text', placeholder: 'Filter...' },
    },
    {
      id: 'authMethod',
      header: 'Auth Method',
      accessor: 'authMethod',
      cell: (value) => {
        const method = value as AuthMethod;
        const variant = method === 'sso' ? 'default' : 'secondary';
        const label = method === 'sso' ? 'SSO' : 'Local';
        return <Badge variant={variant}>{label}</Badge>;
      },
      sortable: true,
    },
    {
      id: 'role',
      header: 'Role',
      accessor: 'role',
      cell: (value) => {
        const role = value as UserRole;
        let variant: 'success' | 'default' | 'secondary';
        if (role === 'SiteAdmin') variant = 'success';
        else if (role === 'DataQualityAdmin') variant = 'default';
        else variant = 'secondary';
        return <Badge variant={variant}>{role}</Badge>;
      },
      sortable: true,
      filter: { type: 'select', options: [{ label: 'SiteAdmin', value: 'SiteAdmin' }, { label: 'DataQualityAdmin', value: 'DataQualityAdmin' }, { label: 'User', value: 'User' }] },
    },
    {
      id: 'createdAt',
      header: 'Created At',
      accessor: 'createdAt',
      cell: (value) => formatDateTime(value as string),
      sortable: true,
    },
    {
      id: 'updatedAt',
      header: 'Updated At',
      accessor: 'updatedAt',
      cell: (value) => formatDateTime(value as string),
      sortable: true,
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: 'id',
      align: 'right',
      cell: (_, user) => (
        <div className="flex items-center justify-end gap-2">
          {!user.isBootstrap && (
            <>
              <select
                className="h-8 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={user.role}
                onChange={(e) => void onRoleChange(user, e.target.value as UserRole)}
                title="Change role"
              >
                <option value="User">User</option>
                <option value="DataQualityAdmin">DataQualityAdmin</option>
                <option value="SiteAdmin">SiteAdmin</option>
              </select>
              <Button
                variant="ghost"
                size="sm"
                className="text-error hover:text-error/80 hover:bg-error/10"
                onClick={() => void onDelete(user)}
                title="Delete User"
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            </>
          )}
          {user.isBootstrap && <span className="text-muted-foreground text-xs italic">System</span>}
        </div>
      ),
    },
  ];
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AdminUsersPage() {
  const { data: users, loading, error, refetch } = useApi(() => userApi.getAll(), []);
  const [pageError, setPageError] = useState<string | null>(null);

  // Dialog State
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    role: 'User' as UserRole,
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleRoleChange = useCallback(async (user: IUser, newRole: UserRole) => {
    if (newRole === user.role) return;
    try {
      await userApi.updateRole(user.id, newRole);
      refetch();
    } catch (err: unknown) {
      setPageError(getErrorMessage(err, 'Failed to update user role'));
    }
  }, [refetch]);

  const handleDelete = useCallback(async (user: IUser) => {
    if (!confirm(`Are you sure you want to delete user ${user.email}?`)) return;
    try {
      await userApi.delete(user.id);
      refetch();
    } catch (err: unknown) {
      setPageError(getErrorMessage(err, 'Failed to delete user'));
    }
  }, [refetch]);

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError(null);
    try {
      await userApi.create(createForm);
      setIsDialogOpen(false);
      setCreateForm({ email: '', password: '', role: 'User' });
      refetch();
    } catch (err: unknown) {
      setCreateError(getErrorMessage(err, 'Failed to create user'));
    } finally {
      setCreateLoading(false);
    }
  };

  const columns = useMemo(
    () => createUserColumns(handleRoleChange, handleDelete),
    [handleRoleChange, handleDelete]
  );

  if (loading) return <PageLoading />;

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader
        title="Users"
        subtitle="Manage user access and roles."
        action={<Button onClick={() => setIsDialogOpen(true)}>Create User</Button>}
      />

      <ErrorBanner message={error || pageError} />

      <DataTable
        data={users ?? []}
        columns={columns}
        keyExtractor={(user) => user.id}
        enablePagination
        enableFilters
        emptyMessage="No users found."
      />

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} title="Create New User">
        <ErrorBanner message={createError} className="mb-4" />

        <form onSubmit={handleCreateSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              className={inputClass}
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              className={inputClass}
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" htmlFor="role">
              Role
            </label>
            <select
              id="role"
              className={inputClass}
              value={createForm.role}
              onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as UserRole })}
            >
              <option value="User">User</option>
              <option value="DataQualityAdmin">DataQualityAdmin</option>
              <option value="SiteAdmin">SiteAdmin</option>
            </select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsDialogOpen(false)}
              disabled={createLoading}
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={createLoading}>
              Create User
            </Button>
          </DialogFooter>
        </form>
      </Dialog>
    </PageContainer>
  );
}
